/**
 * Instrument Master Admin Page
 * 
 * Comprehensive admin interface for managing instrument data, broker mappings, and sync operations.
 * 
 * Features:
 * - Search & Browse instruments with advanced filters
 * - View broker-specific symbol mappings in comparison table
 * - Trigger and monitor sync operations from various sources
 * - Real-time sync status and progress tracking
 * - Scheduled runs management with timezone support (Server: UTC, Display: Local)
 * - Broker priority configuration
 * - Data reconciliation dashboard
 * 
 * Architecture:
 * - Each tab is a separate component in the ./tabs directory for better maintainability
 * - Tabs can be developed, tested, and modified independently
 * - New tabs can be easily added by creating a component and adding to the tabs array
 * 
 * Access: Admin-only (requires 'admin' scope)
 * 
 * @module InstrumentMaster
 */

import React, { useState, useEffect } from 'react';
import { PageHeader, Tabs } from '../../../components/ds';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  FiSearch, 
  FiLink, 
  FiAlertCircle,
  FiClock,
  FiSettings,
  FiGitMerge
} from 'react-icons/fi';

// Import modular tab components
import {
  SearchBrowseTab,
  BrokerMappingsTab,
  ScheduledRunsTab,
  BrokerPriorityTab,
  ReconciliationTab
} from './tabs';

/**
 * Main Instrument Master Admin Component
 * 
 * Renders a tabbed interface for instrument management with admin access control.
 * Each tab is a separate component that can be independently maintained.
 */
const InstrumentMaster = () => {
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('search');

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      showSnackbar('Access denied. Admin privileges required.', 'error');
    }
  }, [user, showSnackbar]);

  /**
   * Tab configuration
   * Add new tabs here by providing id, name, icon, and component
   */
  const tabs = [
    { id: 'search', name: 'Search & Browse', icon: FiSearch, component: SearchBrowseTab },
    { id: 'mappings', name: 'Broker Mappings', icon: FiLink, component: BrokerMappingsTab },
    { id: 'schedules', name: 'Scheduled Runs', icon: FiClock, component: ScheduledRunsTab },
    { id: 'priority', name: 'Broker Priority', icon: FiSettings, component: BrokerPriorityTab },
    { id: 'reconciliation', name: 'Reconciliation', icon: FiGitMerge, component: ReconciliationTab },
  ];

  // Get the active tab's component
  const ActiveTabComponent = tabs.find(tab => tab.id === activeTab)?.component;

  return (
    <div className="min-h-screen bg-main-bg dark:bg-main-dark-bg pt-0 px-4 md:px-6 pb-10">
      {/* Standardized page header (common across all pages) */}
      <PageHeader
        title="Instrument Master"
        subtitle="Manage instrument data, broker mappings, and sync operations"
      />

      {/* Admin Access Warning */}
      {user && user.role !== 'admin' && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <FiAlertCircle className="text-red-600 dark:text-red-400 text-xl flex-shrink-0" />
          <div>
            <p className="text-red-800 dark:text-red-200 font-semibold">
              Admin Access Required
            </p>
            <p className="text-red-600 dark:text-red-400 text-sm">
              You do not have permission to access this page. Contact your administrator.
            </p>
          </div>
        </div>
      )}

      {/* Tabs Navigation (DS underline tabs) */}
      <Tabs
        className="mb-6"
        value={activeTab}
        onChange={setActiveTab}
        tabs={tabs.map((tab) => {
          const Icon = tab.icon;
          return {
            label: tab.name,
            value: tab.id,
            icon: <Icon className="text-lg" />,
          };
        })}
      />

      {/* Tab Content - Render active tab component */}
      {user && user.role === 'admin' && ActiveTabComponent && (
        <div className="mt-6">
          <ActiveTabComponent />
        </div>
      )}
    </div>
  );
};

export default InstrumentMaster;
